import type { TemplateQuestion } from '../api/types'
import { findCountry } from '../constants/countries'

/**
 * ONE place that answers "what kind of control is this question?".
 *
 * THE BUG THIS EXISTS TO KILL. The database column is `question_type`, but
 * `ApplicationSupabaseService.shapeTemplate` renames it on the way out:
 *
 *     shaped.put("type", question.get("question_type"));
 *
 * The web onboarding read `question.question_type`, which is therefore always
 * undefined over the API. Every comparison against 'multi_select' was false,
 * so EVERY question rendered as a single-select — and single-select
 * auto-advances on tap. That one wrong property name is why `looking_for`,
 * `hobbies`, `languages_spoken`, `qualities` and `food_restrictions` all
 * arrived at the backend as bare strings instead of arrays, why the date of
 * birth was a free-text field, and why the restaurant question accepted the
 * word "none".
 *
 * Reading both, with `type` winning, so this cannot break again from either
 * direction — and so a template fetched straight from PostgREST (which does
 * carry `question_type`) still works.
 */
export function questionType(question: TemplateQuestion | null | undefined): string {
  return String(question?.type || question?.question_type || 'text')
}

export const isMultiSelect = (question: TemplateQuestion | null | undefined): boolean =>
  questionType(question) === 'multi_select'

export const isSingleSelect = (question: TemplateQuestion | null | undefined): boolean =>
  questionType(question) === 'single_select'

/**
 * `favorite_restaurant_sh` predates the `restaurant_finder` type, so it is
 * matched by key as well. Same rule the mini program uses.
 */
export const isRestaurantQuestion = (question: TemplateQuestion | null | undefined): boolean =>
  questionType(question) === 'restaurant_finder' || question?.key === 'favorite_restaurant_sh'

/** How many choices a multi_select demands, and allows. 0 max = no ceiling. */
export function choiceBounds(question: TemplateQuestion | null | undefined): { min: number; max: number } {
  const rules = (question?.validation_rules || {}) as Record<string, unknown>
  const required = Boolean(question?.is_required)
  const min = Number(rules.min_choices ?? rules.min_selected ?? (required && isMultiSelect(question) ? 1 : 0)) || 0
  const max = Number(rules.max_choices ?? 0) || 0
  return { min, max }
}

/** The stops of a `scale` question, e.g. introverted → ambiverted → extroverted. */
export function scaleStops(question: TemplateQuestion | null | undefined): string[] {
  const rules = (question?.validation_rules || {}) as Record<string, unknown>
  const stops = rules.stops
  if (Array.isArray(stops) && stops.length) return stops.map(String)
  return (question?.options || []).map((option) => option.value)
}

/**
 * Seed answers with the demographics that live on the PROFILE.
 *
 * WHY THIS IS NOT OPTIONAL. `ApplicationSupabaseService.syncDemographics`
 * copies four answers out of the questionnaire and onto `profiles`:
 *
 *     gender, date_of_birth, industry,
 *     origin.country -> nationality_country, origin.region -> home_region
 *
 * They are answers AND profile columns. A flow that reads only
 * `application.answers` therefore cannot see them, and the resume index —
 * "first question with no key in answers" — lands right back on gender. That
 * is the bug where you pick Man, press next, refresh, and are asked again.
 *
 * The mini program has always merged these (withDemographics in
 * OnboardingFlow.jsx). Shared here so the two apps resume identically.
 *
 * Existing answers always win: a value the member just changed must not be
 * overwritten by the older copy sitting on their profile.
 */
export function withProfileDemographics(
  answers: Record<string, unknown> | null | undefined,
  profile: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!profile) return answers || {}
  const merged: Record<string, unknown> = { ...(answers || {}) }

  if (merged.gender === undefined && profile.gender) merged.gender = profile.gender
  if (merged.date_of_birth === undefined && profile.date_of_birth) merged.date_of_birth = profile.date_of_birth
  if (merged.industry === undefined && profile.industry) merged.industry = profile.industry
  if (merged.origin === undefined && (profile.nationality_country || profile.home_region)) {
    merged.origin = {
      ...(profile.nationality_country ? { country: profile.nationality_country } : {}),
      ...(profile.home_region ? { region: profile.home_region } : {})
    }
  }
  return merged
}

/**
 * Why an answer is not acceptable yet, or null when it is.
 *
 * Deliberately returns a REASON rather than a boolean. The web app used to
 * check nothing per question and let everything fail at the final submit, so
 * a member filled in twenty screens and got one opaque 500 with no clue which
 * answer was wrong. A question can now refuse to advance and say why.
 *
 * Pure and platform-free so the mini program, the web app, and any future
 * React Native app cannot disagree about what "valid" means.
 */
export function validateAnswer(
  question: TemplateQuestion | null | undefined,
  value: unknown,
  lang: 'en' | 'zh' = 'en'
): string | null {
  if (!question) return null
  const zh = lang === 'zh'
  const required = Boolean(question.is_required)
  const type = questionType(question)

  const empty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)

  if (empty) {
    return required ? (zh ? '请先回答这个问题' : 'Please answer this question') : null
  }

  if (type === 'multi_select') {
    // A multi_select answer MUST be an array. Coercing a bare string into
    // `[value]` here would have quietly waved through exactly the payload that
    // caused this: `looking_for: "business_network"` instead of
    // `["business_network"]`. The column is a JSON array; a string is not one
    // element of it, it is the wrong type.
    if (!Array.isArray(value)) {
      return zh ? '请选择选项' : 'Select from the options'
    }
    const list = value
    const { min, max } = choiceBounds(question)
    if (min && list.length < min) {
      return zh ? `至少选择 ${min} 项` : `Select at least ${min}`
    }
    if (max && list.length > max) {
      return zh ? `最多选择 ${max} 项` : `Select up to ${max}`
    }
    return null
  }

  // A restaurant is only an answer once it carries a real restaurants.id.
  // Anything else cannot be linked by foreign key later, which is precisely
  // how the string "none" ended up in `favorite_restaurant_sh`.
  if (isRestaurantQuestion(question)) {
    const picked = value as Record<string, unknown> | null
    const hasId = Boolean(picked && typeof picked === 'object' && (picked.id || picked.restaurant_id))
    if (!hasId) {
      return required
        ? (zh ? '请从列表中选择一家餐厅' : 'Pick a restaurant from the list')
        : null
    }
    return null
  }

  if (type === 'country_region') {
    const code = typeof value === 'string' ? value : (value as { country?: string })?.country
    // It has to be a REAL ISO code, not a typed word. The broken payload held
    // `origin: "france"`, which looks answered and resolves to nothing — a
    // country picker that accepts free text is not a picker.
    if (!code || !findCountry(code)) {
      return required || code
        ? (zh ? '请从列表中选择国家或地区' : 'Pick a country or region from the list')
        : null
    }
    return null
  }

  if (type === 'month_year' || type === 'date') {
    // Stored as YYYY-MM-01. "13/04/1990" is what a free-text box produces and
    // what Postgres then refuses as a date.
    if (!/^\d{4}-\d{2}(-\d{2})?$/.test(String(value))) {
      return zh ? '请选择日期' : 'Pick a date'
    }
    return null
  }

  return null
}
